/**
 * Microphone Permission Utilities
 * Core functions for device detection, permission checking, and platform-specific instructions
 * Adapted from React solution to vanilla JS
 */

/**
 * Detects device and browser information
 */
export function getDeviceInfo() {
  const ua = navigator.userAgent;

  // OS Detection
  const isIOS =
    /iPad|iPhone|iPod/.test(ua) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const isAndroid = /Android/.test(ua);
  const isMac = /Mac/.test(ua) && !isIOS;
  const isWindows = /Win/.test(ua);

  // Browser Detection
  const isChrome = /Chrome/.test(ua) && !/Edg|OPR/.test(ua);
  const isSafari = /Safari/.test(ua) && !/Chrome/.test(ua);
  const isFirefox = /Firefox/.test(ua);
  const isEdge = /Edg/.test(ua);
  const isSamsung = /SamsungBrowser/.test(ua);

  // On iOS, all browsers use Safari's WebKit engine for permissions
  let browser = 'unknown';
  if (isIOS) {
    browser = 'safari';
  } else if (isSamsung) {
    browser = 'samsung';
  } else if (isChrome) {
    browser = 'chrome';
  } else if (isSafari) {
    browser = 'safari';
  } else if (isFirefox) {
    browser = 'firefox';
  } else if (isEdge) {
    browser = 'edge';
  }

  let platform = 'unknown';
  if (isIOS) {
    platform = 'ios';
  } else if (isAndroid) {
    platform = 'android';
  } else if (isMac) {
    platform = 'mac';
  } else if (isWindows) {
    platform = 'windows';
  }

  return {
    platform,
    browser,
    isIOS,
    isAndroid,
    isMac,
    isWindows,
    isMobile: isIOS || isAndroid,
    isDesktop: !isIOS && !isAndroid,
  };
}

/**
 * Check microphone permission state using Permissions API
 * Returns 'granted', 'denied', 'prompt', or 'unknown'
 */
export async function checkPermissionState() {
  try {
    if (navigator.permissions && navigator.permissions.query) {
      const result = await navigator.permissions.query({ name: 'microphone' });
      return result.state;
    }
  } catch (error) {
    // Permissions API might not be supported or might throw for microphone
    // Firefox doesn't support this query, some browsers don't support 'microphone' name
    console.log('⚠️ MicPermissionUtils: Permissions API not available or not supported for microphone');
  }
  return 'unknown'; // Unknown state if API not available
}

/**
 * Get platform and browser specific instructions for enabling microphone
 */
export function getPermissionInstructions(deviceInfo) {
  const { platform, browser } = deviceInfo;

  const instructions = {
    ios: {
      title: 'Enable Microphone on iPhone/iPad',
      icon: '📱',
      steps: [
        'Tap the <strong>lock icon 🔒</strong>, <strong>information icon ℹ️</strong>, or <strong>AA icon</strong> in the address bar',
        'Tap <strong>Website Settings</strong> or <strong>Permissions</strong>',
        'Find <strong>Microphone</strong> and set it to <strong>Allow</strong>',
        'Return to this page and refresh',
      ],
      altTitle: 'Alternative Method',
      altSteps: [
        'Open the <strong>Settings</strong> app on your device',
        'Scroll down and tap <strong>Safari</strong> (or your browser app)',
        'Tap <strong>Microphone</strong> under "Settings for Websites"',
        'Set it to <strong>Allow</strong> or <strong>Ask</strong>',
        'Return to this page and refresh',
      ],
      note: 'On iOS, you can also change permissions in the browser address bar or in device Settings.',
      canOpenSettings: true,
      settingsButtonText: 'Open Settings App',
    },

    android_chrome: {
      title: 'Enable Microphone in Chrome',
      icon: '🤖',
      steps: [
        'Tap the <strong>lock icon 🔒</strong>, <strong>information icon ℹ️</strong>, or <strong>settings icon ⋮</strong> in the address bar',
        'Tap <strong>Permissions</strong>',
        'Tap <strong>Microphone</strong>',
        'Select <strong>Allow</strong>',
        'The page will refresh automatically',
      ],
      altTitle: 'Alternative Method',
      altSteps: [
        'Tap the <strong>⋮ menu</strong> (three dots)',
        'Tap <strong>Settings → Site settings → Microphone</strong>',
        'Find this website and set to <strong>Allow</strong>',
      ],
      canOpenSettings: true,
      settingsButtonText: 'Open App Settings',
    },

    android_firefox: {
      title: 'Enable Microphone in Firefox',
      icon: '🦊',
      steps: [
        'Tap the <strong>lock icon 🔒</strong> or <strong>information icon ℹ️</strong> in the address bar',
        'Tap <strong>Edit Site Permissions</strong>',
        'Toggle <strong>Microphone</strong> to enabled',
        'Refresh the page',
      ],
      canOpenSettings: false,
    },

    android_samsung: {
      title: 'Enable Microphone in Samsung Browser',
      icon: '📱',
      steps: [
        'Tap the <strong>menu icon</strong> (three lines)',
        'Tap <strong>Settings → Sites and downloads</strong>',
        'Tap <strong>Site permissions → Microphone</strong>',
        'Find this website and set to <strong>Allow</strong>',
        'Return and refresh the page',
      ],
      canOpenSettings: false,
    },

    desktop_chrome: {
      title: 'Enable Microphone in Chrome',
      icon: '🌐',
      steps: [
        'Click the <strong>lock icon 🔒</strong> or <strong>information icon ℹ️</strong> in the address bar (left side)',
        'Find <strong>Microphone</strong> in the dropdown menu',
        'Change it from "Block" to <strong>Allow</strong>',
        'Click the <strong>Refresh</strong> button or press F5',
      ],
      showAddressBarHint: true,
      canOpenSettings: false,
    },

    desktop_safari: {
      title: 'Enable Microphone in Safari',
      icon: '🧭',
      steps: [
        'Click <strong>Safari</strong> in the menu bar (top of screen)',
        'Click <strong>Settings for This Website...</strong>',
        'Find <strong>Microphone</strong> in the popup',
        'Change it to <strong>Allow</strong>',
        'Close settings and refresh the page',
      ],
      canOpenSettings: false,
    },

    desktop_firefox: {
      title: 'Enable Microphone in Firefox',
      icon: '🦊',
      steps: [
        'Click the <strong>lock icon 🔒</strong> in the address bar',
        'Click the <strong>X</strong> next to "Blocked" for Microphone',
        'Refresh the page (press F5)',
        'Click <strong>Allow</strong> when prompted again',
      ],
      note: 'Firefox requires you to clear the block and then re-allow on the next prompt.',
      showAddressBarHint: true,
      canOpenSettings: false,
    },

    desktop_edge: {
      title: 'Enable Microphone in Edge',
      icon: '🌐',
      steps: [
        'Click the <strong>lock icon 🔒</strong> in the address bar',
        'Find <strong>Microphone</strong> in the permissions list',
        'Change it to <strong>Allow</strong>',
        'Refresh the page',
      ],
      showAddressBarHint: true,
      canOpenSettings: false,
    },

    unknown: {
      title: 'Enable Microphone Access',
      icon: '🎤',
      steps: [
        'Look for a <strong>lock icon 🔒</strong>, <strong>information icon ℹ️</strong>, or <strong>settings icon</strong> in your browser\'s address bar',
        'Find the <strong>Microphone</strong> permission setting',
        'Change it to <strong>Allow</strong>',
        'Refresh this page',
      ],
      canOpenSettings: false,
    },
  };

  // Determine which instruction set to use
  if (platform === 'ios') {
    return instructions.ios;
  }

  if (platform === 'android') {
    const key = `android_${browser}`;
    return instructions[key] || instructions.android_chrome;
  }

  // Desktop
  const key = `desktop_${browser}`;
  return instructions[key] || instructions.desktop_chrome;
}

/**
 * Attempts to open device settings (mobile only)
 * Returns true if attempted, false if not supported
 */
export function openSettings(deviceInfo) {
  const { isIOS, isAndroid, browser } = deviceInfo;

  if (isIOS) {
    // Opens iOS Settings app
    try {
      window.location.href = 'app-settings:';
      return true;
    } catch (e) {
      console.warn('Could not open iOS settings:', e);
      return false;
    }
  }

  if (isAndroid) {
    // Try to open Chrome's app settings on Android
    if (browser === 'chrome') {
      try {
        window.location.href =
          'intent:#Intent;action=android.settings.APPLICATION_DETAILS_SETTINGS;data=package:com.android.chrome;end';
        return true;
      } catch (e) {
        console.warn('Could not open Android settings:', e);
        return false;
      }
    }
  }

  return false;
}

